import { Injectable } from '@angular/core';
import { first, from, Observable } from 'rxjs';
import { AlertController } from '@ionic/angular';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface ConfirmModalData {
  title: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  titleArgs?: Object;
  message: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  messageArgs?: Object;
  confirmButtonText: string;
  cancelButtonText: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmDialogService {
  readonly confirmRole = 'confirm';
  readonly cancelRole = 'cancel';

  constructor(
    private alertController: AlertController,
    private translationService: TranslateService
  ) {}

  public showDialog(data: ConfirmModalData): Observable<boolean> {
    return from(
      this.alertController.create({
        header: this.translationService.instant(data.title, data.titleArgs),
        message: this.translationService.instant(
          data.message,
          data.messageArgs
        ),
        cssClass: 'confirm-dialog',
        buttons: [
          {
            text: this.translationService.instant(data.cancelButtonText),
            role: this.cancelRole,
          },
          {
            text: this.translationService.instant(data.confirmButtonText),
            role: this.confirmRole,
          },
        ],
      })
    ).pipe(
      switchMap((modal) => from(modal.present()).pipe(map(() => modal))),
      switchMap((modal) => from(modal.onDidDismiss())),
      map((result) => result.role === this.confirmRole)
    );
  }

  public showAlertDialog(title: string, message: string): Observable<unknown> {
    return from(
      this.alertController.create({
        header: this.translationService.instant(title),
        message: this.translationService.instant(message),
        buttons: ['OK'],
      })
    ).pipe(
      first(),
      switchMap((modal) => from(modal.present()).pipe(map(() => modal))),
      switchMap((modal) => from(modal.onDidDismiss()))
    );
  }
}
